import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  const navigationItems = [
    { path: '/homepage', label: 'Home', icon: 'Home' },
    { path: '/menu-showcase', label: 'Menu', icon: 'UtensilsCrossed' },
    { path: '/catering-excellence', label: 'Catering', icon: 'ChefHat' },
    { path: '/our-italian-story', label: 'Our Story', icon: 'Heart' },
    { path: '/visit-connect', label: 'Visit Us', icon: 'MapPin' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-background/95 backdrop-blur-md shadow-warm' 
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            <Link 
              to="/homepage" 
              className="flex items-center gap-3 group"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-primary/20">
                <Icon 
                  name="Croissant" 
                  size={28} 
                  color="var(--color-primary)" 
                  className="transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-accent text-2xl text-primary leading-none">
                  Montesanto
                </span>
                <span className="text-xs text-muted-foreground tracking-wider uppercase">
                  Artisan Bakery
                </span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link to="/gallery-reviews">
                <Button variant="outline" iconName="Star" iconPosition="left">
                  Reviews
                </Button>
              </Link>
              <Button variant="default" iconName="ShoppingBag" iconPosition="left">
                Order Now
              </Button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? 'X' : 'Menu'} 
                size={24} 
                color="var(--color-foreground)"
              />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <div 
        className={`fixed top-20 right-0 w-80 h-[calc(100vh-5rem)] bg-card shadow-warm-lg z-40 lg:hidden transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <nav className="flex flex-col p-6 gap-2">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 flex items-center gap-3 ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted hover:text-primary'
              }`}
            >
              <Icon name={item?.icon} size={20} />
              <span>{item?.label}</span>
            </Link>
          ))}
          
          <div className="border-t border-border my-4" />
          
          <Link to="/gallery-reviews" className="w-full">
            <Button 
              variant="outline" 
              iconName="Star" 
              iconPosition="left"
              fullWidth
            >
              Reviews & Gallery
            </Button>
          </Link>
          
          <Button 
            variant="default" 
            iconName="ShoppingBag" 
            iconPosition="left"
            fullWidth
          >
            Order Now
          </Button>
        </nav>
      </div>
    </>
  );
};

export default Header;